import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { MessageService } from 'primeng/api';
import { forkJoin, of, Subject } from 'rxjs';
import { catchError, finalize, takeUntil } from 'rxjs/operators';

import { GerinusBD } from 'src/app/app-core/lib/gerinus-bd.component';
import { GlobalService } from 'src/app/app-core/lib/global.service';
import { ProcessService } from 'src/app/app-core/services/process.service';
import { InternalProcessService } from 'src/app/app-core/services/internal-process.service';
import { DocumentSignatureService } from 'src/app/app-core/services/document-signature.service';
import { UploadService } from 'src/app/app-core/services/upload.service';
import { environment } from 'src/environments/environment';
import { SignedFileDataResponse } from 'src/app/interfaces/signature-gov.interfaces';
import { DigitalFormService } from 'src/app/app-core/services/digital-form.service';
import { DigitalFormSignatureStatusEnum } from 'src/app/Enums/DigitalFormEnum';
import { IDigitalFormRequerimentoItem } from 'src/app/interfaces/digital-form';

type ParecerOrigin = 'externo' | 'interno';

/** Tipo de documento na fila de assinatura do(a) secretário(a). */
type DocumentoTipo = 'parecer' | 'certidao' | 'requerimento';

interface ISecretaryParecerFilters {
    numeroProcesso: string;
    interessado: string;
    ementa: string;
    dataRange: Date[] | undefined;
}

@Component({
    selector: 'app-secretary-pending-signatures',
    templateUrl: './secretary-pending-signatures.component.html',
    styleUrls: ['./secretary-pending-signatures.component.scss'],
    providers: [
        MessageService,
        ProcessService,
        InternalProcessService,
        DocumentSignatureService,
        UploadService,
        DigitalFormService,
    ],
})
export class SecretaryPendingSignaturesComponent extends GerinusBD implements OnInit, OnDestroy {
    public customTitle: string = 'Assinaturas Pendentes';

    public isFiltersModalOpen: boolean = false;
    public isFiltersModalResponsiveOpen: boolean = false;
    public filtersModel: ISecretaryParecerFilters = this.defaultFilters();
    public appliedFilters: ISecretaryParecerFilters = this.defaultFilters();

    public isSigning: boolean = false;
    public signingMessage: string = '';
    public signingParecerId: number | null = null;

    /** Anulação de requerimento digital, no lugar da assinatura. */
    public isAnnulDialogOpen = false;
    public isAnnulling = false;
    public annulTarget: any = null;
    public annulReason = '';
    public annulError: string | null = null;
    public readonly minAnnulReason = 10;

    public isParecerViewerOpen: boolean = false;
    public isParecerViewerLoading: boolean = false;
    public parecerViewerUrl: SafeResourceUrl | null = null;
    public parecerViewerTitle: string = 'Visualizar Documento';
    private parecerViewerBlobUrl: string | null = null;

    private destroy$ = new Subject<void>();
    private popupPollInterval: any = null;
    private boundMessageListener: ((event: MessageEvent) => void) | null = null;
    private pendingSignContext:
        | {
              parecerId: number;
              origin: ParecerOrigin;
              tipo: DocumentoTipo;
              pdfBase64: string;
              fileName: string;
              pageCount: number;
              signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null;
          }
        | null = null;

    constructor(
        public global: GlobalService,
        public processService: ProcessService,
        private internalProcessService: InternalProcessService,
        private digitalFormService: DigitalFormService,
        private documentSignatureService: DocumentSignatureService,
        private uploadService: UploadService,
        private sanitizer: DomSanitizer,
        private messageService: MessageService,
        private ngZone: NgZone,
    ) {
        super(global, processService);
        this.showPaginate = true;
        this.showEmptyData = true;
        this.emptyTitle = 'Nenhum documento encontrado';
        this.emptyText = 'Não há documentos para os filtros e situação selecionados.';
    }

    public emptyTitle: string;
    public emptyText: string;

    ngOnInit(): void {
        this.listAll();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.clearPopupPoll();
    }

    public listAll(page: number = 1): void {
        const userId = this.global.user()?.USR_CodigoUsuario;
        if (!userId) {
            (this as any).list = [];
            return;
        }

        (this as any).list = null;
        (this as any).pagination = null;

        const filters = this.serializeFilters(this.appliedFilters);
        const emptyResult = { data: [], meta: { total: 0, per_page: 10, current_page: page, last_page: 1 } };

        forkJoin({
            externo: this.processService
                .listParecerAllForSecretary(userId, page, 10, filters)
                .pipe(catchError(() => of(emptyResult))),
            interno: this.internalProcessService
                .listParecerAllForSecretary(userId, page, 10, filters)
                .pipe(catchError(() => of(emptyResult))),
            certidao: this.processService
                .listCertidaoAllForSecretary(userId, page, 10, filters)
                .pipe(catchError(() => of(emptyResult))),
            requerimentos: this.digitalFormService
                .listForResponsible(page, 10, { ...filters, assunto: filters.ementa })
                .pipe(catchError(() => of(emptyResult))),
        })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ externo, interno, certidao, requerimentos }) => {
                    const externalItems = (externo?.data || []).map((item: any) => ({
                        ...item,
                        __origin: 'externo' as ParecerOrigin,
                        __tipo: 'parecer' as DocumentoTipo,
                    }));
                    const internalItems = (interno?.data || []).map((item: any) => this.normalizeInternalParecer(item));
                    const certidaoItems = (certidao?.data || []).map((item: any) => this.normalizeCertidao(item));
                    const requerimentoItems = (requerimentos?.data || []).map((item: any) => this.normalizeRequerimento(item));

                    const merged = [...externalItems, ...internalItems, ...certidaoItems, ...requerimentoItems].sort((a, b) => {
                        const aPending = this.isPending(a) ? 0 : 1;
                        const bPending = this.isPending(b) ? 0 : 1;
                        if (aPending !== bPending) return aPending - bPending;
                        return this.parecerSortDate(b) - this.parecerSortDate(a);
                    });

                    (this as any).list = merged;
                    (this as any).pagination = {
                        total:
                            (externo?.meta?.total || 0) +
                            (interno?.meta?.total || 0) +
                            (certidao?.meta?.total || 0) +
                            (requerimentos?.meta?.total || 0),
                        per_page: 10,
                        current_page: page,
                        last_page: Math.max(
                            externo?.meta?.last_page || 1,
                            interno?.meta?.last_page || 1,
                            certidao?.meta?.last_page || 1,
                            requerimentos?.meta?.last_page || 1,
                        ),
                    };
                    this.updateEmptyTexts();
                },
                error: () => {
                    (this as any).list = [];
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Não foi possível carregar os documentos.',
                    });
                },
            });
    }

    /**
     * Normaliza um parecer de processo INTERNO (campos PIPD_/PI_) para o mesmo
     * formato (PPD_/PRC_) usado pelo template e mantém a referência de origem.
     */
    /**
     * Normaliza uma CERTIDÃO (campos PC_) para o mesmo formato (PPD_/PRC_) usado
     * pelo template. A certidão só existe em processo externo.
     */
    private normalizeCertidao(item: any): any {
        const numero = (item.PC_NumeroLicenca || '').toString().trim();
        const assunto = (item.PC_Assunto || '').toString().trim();

        return {
            ...item,
            __origin: 'externo' as ParecerOrigin,
            __tipo: 'certidao' as DocumentoTipo,
            PPD_CodigoParecerDespacho: item.PC_CodigoCertidao,
            PPD_StatusRascunho: item.PC_StatusRascunho,
            PPD_Anexo: item.PC_Anexo,
            PPD_NomeAnexo: item.PC_Nome,
            PPD_Interessado: item.PC_NomeRazaoSocial,
            PPD_Assunto: assunto,
            PPD_Ementa: [assunto, numero ? `N° ${numero}` : ''].filter((part) => part.length > 0).join(' '),
            PPD_NomeSecretario: item.PC_NomeSecretario,
            PPD_DataAssinatura: item.PC_DataAssinatura,
            PPD_DataAssinaturaSecretario: item.PC_DataAssinaturaSecretario,
        };
    }

    private normalizeInternalParecer(item: any): any {
        return {
            ...item,
            __origin: 'interno' as ParecerOrigin,
            __tipo: 'parecer' as DocumentoTipo,
            PPD_CodigoParecerDespacho: item.PIPD_CodigoParecerDespacho,
            PPD_StatusRascunho: item.PIPD_StatusRascunho,
            PPD_Anexo: item.PIPD_Anexo,
            PPD_NomeAnexo: item.PIPD_NomeAnexo,
            PPD_Interessado: item.PIPD_Interessado,
            PPD_Assunto: item.PIPD_Assunto,
            PPD_Ementa: item.PIPD_Ementa,
            PPD_NomeSecretario: item.PIPD_NomeSecretario,
            PPD_DataAssinatura: item.PIPD_DataAssinatura,
            PPD_DataAssinaturaSecretario: item.PIPD_DataAssinaturaSecretario,
            PRC_NumeroProcesso: item.PI_NumeroProcessoInterno,
        };
    }

    /**
     * Normaliza um requerimento de formulário digital (campos SFR_) para o mesmo
     * formato PPD_/PRC_ usado pelo template, preservando a origem para a assinatura.
     */
    private normalizeRequerimento(item: IDigitalFormRequerimentoItem): any {
        const status =
            item.SFR_StatusAssinatura === DigitalFormSignatureStatusEnum.AGUARDANDO_RESPONSAVEL
                ? 'AGUARDANDO_SECRETARIO'
                : item.SFR_StatusAssinatura === DigitalFormSignatureStatusEnum.ANULADO
                ? 'ANULADO'
                : 'ASSINADO';

        return {
            ...item,
            __origin: 'externo' as ParecerOrigin,
            __tipo: 'requerimento' as DocumentoTipo,
            __isRequerimento: true,
            PPD_CodigoParecerDespacho: item.SFR_CodigoRequerimento,
            PPD_StatusRascunho: status,
            PPD_Anexo: item.SFR_Anexo,
            PPD_NomeAnexo: item.SFR_NomeAnexo,
            PPD_Interessado: item.Interessado,
            PPD_Assunto: item.AS_Descricao,
            PPD_Ementa: item.TituloFormulario || item.AS_Descricao || '',
            PPD_DataAssinatura: item.SFR_DataAssinaturaCidadao,
            PPD_DataAssinaturaSecretario: item.SFR_DataAssinaturaResponsavel,
            PRC_NumeroProcesso: item.PRC_NumeroProcesso,
        };
    }

    private parecerSortDate(item: any): number {
        const value = item?.PPD_DataAssinaturaSecretario || item?.PPD_DataAssinatura || item?.created_at;
        const time = value ? new Date(value).getTime() : 0;
        return isNaN(time) ? 0 : time;
    }

    public isPending(item: any): boolean {
        return item?.PPD_StatusRascunho === 'AGUARDANDO_SECRETARIO';
    }

    public isAnnulled(item: any): boolean {
        return item?.PPD_StatusRascunho === 'ANULADO';
    }

    public statusLabel(item: any): string {
        if (this.isPending(item)) return 'Aguardando assinatura';
        if (this.isAnnulled(item)) return 'Anulado';
        return 'Assinado';
    }

    // ------------------------------------------------------------------
    // Anulação do requerimento pelo responsável
    // ------------------------------------------------------------------

    public openAnnul(item: any): void {
        if (!item?.__isRequerimento || !this.isPending(item) || this.isSigning) return;

        this.annulTarget = item;
        this.annulReason = '';
        this.annulError = null;
        this.isAnnulDialogOpen = true;
    }

    public closeAnnul(): void {
        if (this.isAnnulling) return;

        this.isAnnulDialogOpen = false;
        this.annulTarget = null;
    }

    public get canConfirmAnnul(): boolean {
        return !this.isAnnulling && this.annulReason.trim().length >= this.minAnnulReason;
    }

    public confirmAnnul(): void {
        if (!this.canConfirmAnnul || !this.annulTarget) return;

        this.isAnnulling = true;
        this.annulError = null;

        this.digitalFormService
            .annul(this.annulTarget.PPD_CodigoParecerDespacho, this.annulReason.trim())
            .pipe(takeUntil(this.destroy$), finalize(() => (this.isAnnulling = false)))
            .subscribe({
                next: (response) => {
                    this.isAnnulDialogOpen = false;
                    this.annulTarget = null;
                    this.messageService.add({
                        severity: 'success',
                        icon: 'ph ph-check-circle',
                        closable: true,
                        summary: 'Solicitação anulada',
                        detail: response?.message || 'O requerente foi avisado do motivo.',
                    });
                    this.listAll((this.pagination as any)?.current_page || 1);
                },
                error: (err) => {
                    this.annulError = err?.error?.message || 'Não foi possível anular a solicitação.';
                },
            });
    }

    public applyFilters(): void {
        this.appliedFilters = { ...this.filtersModel };
        this.isFiltersModalOpen = false;
        this.isFiltersModalResponsiveOpen = false;
        this.listAll(1);
    }

    public clearFilters(): void {
        this.filtersModel = this.defaultFilters();
        this.appliedFilters = this.defaultFilters();
        this.isFiltersModalOpen = false;
        this.isFiltersModalResponsiveOpen = false;
        this.listAll(1);
    }

    public get hasActiveFilters(): boolean {
        return !!(
            this.appliedFilters.numeroProcesso ||
            this.appliedFilters.interessado ||
            this.appliedFilters.ementa ||
            (this.appliedFilters.dataRange && this.appliedFilters.dataRange[0])
        );
    }

    private defaultFilters(): ISecretaryParecerFilters {
        return {
            numeroProcesso: '',
            interessado: '',
            ementa: '',
            dataRange: undefined,
        };
    }

    private serializeFilters(filters: ISecretaryParecerFilters) {
        const result: { numeroProcesso?: string; interessado?: string; ementa?: string; dataInicio?: string; dataFim?: string } = {};
        if (filters.numeroProcesso) result.numeroProcesso = filters.numeroProcesso;
        if (filters.interessado) result.interessado = filters.interessado;
        if (filters.ementa) result.ementa = filters.ementa;
        if (filters.dataRange && filters.dataRange[0]) {
            result.dataInicio = this.toIsoDate(filters.dataRange[0], false);
        }
        if (filters.dataRange && filters.dataRange[1]) {
            result.dataFim = this.toIsoDate(filters.dataRange[1], true);
        }
        return result;
    }

    private toIsoDate(date: Date, endOfDay: boolean): string {
        const d = new Date(date);
        if (endOfDay) {
            d.setHours(23, 59, 59, 999);
        } else {
            d.setHours(0, 0, 0, 0);
        }
        return d.toISOString();
    }

    private updateEmptyTexts(): void {
        this.emptyTitle = 'Nenhum documento encontrado';
        this.emptyText = 'Não há documentos pendentes ou assinados para os filtros selecionados.';
    }

    public openParecer(item: any): void {
        if (!item?.PPD_Anexo) return;

        const tipo = this.tipoLabel(item);

        this.parecerViewerTitle = item.PRC_NumeroProcesso
            ? `${tipo} — ${item.PRC_NumeroProcesso}`
            : `Visualizar ${tipo}`;
        this.revokeParecerViewerBlobUrl();
        this.parecerViewerUrl = null;
        this.isParecerViewerLoading = true;
        this.isParecerViewerOpen = true;

        this.uploadService
            .download(item.PPD_Anexo, 'SIGN')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response: any) => {
                    if (!response?.url) {
                        this.isParecerViewerLoading = false;
                        this.closeParecerViewer();
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Erro',
                            detail: `Não foi possível obter o PDF do ${tipo.toLowerCase()}.`,
                        });
                        return;
                    }

                    this.uploadService
                        .downloadAndReturnArrayBuffer(response.url)
                        .pipe(takeUntil(this.destroy$), finalize(() => (this.isParecerViewerLoading = false)))
                        .subscribe({
                            next: (buffer: ArrayBuffer) => {
                                const blob = new Blob([buffer], { type: 'application/pdf' });
                                const blobUrl = URL.createObjectURL(blob);
                                this.parecerViewerBlobUrl = blobUrl;
                                this.parecerViewerUrl = this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl);
                            },
                            error: () => {
                                this.closeParecerViewer();
                                this.messageService.add({
                                    severity: 'error',
                                    summary: 'Erro',
                                    detail: `Falha ao carregar o PDF do ${tipo.toLowerCase()}.`,
                                });
                            },
                        });
                },
                error: () => {
                    this.isParecerViewerLoading = false;
                    this.closeParecerViewer();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: `Falha ao obter o PDF do ${tipo.toLowerCase()}.`,
                    });
                },
            });
    }

    public closeParecerViewer(): void {
        this.isParecerViewerOpen = false;
        this.parecerViewerUrl = null;
        this.isParecerViewerLoading = false;
        this.revokeParecerViewerBlobUrl();
    }

    private revokeParecerViewerBlobUrl(): void {
        if (this.parecerViewerBlobUrl) {
            try {
                URL.revokeObjectURL(this.parecerViewerBlobUrl);
            } catch {
                /* noop */
            }
            this.parecerViewerBlobUrl = null;
        }
    }

    public tipoLabel(item: any): string {
        return this.tipoNome(item?.__tipo);
    }

    private tipoNome(tipo: DocumentoTipo | undefined): string {
        if (tipo === 'certidao') return 'Certidão';
        if (tipo === 'requerimento') return 'Requerimento';

        return 'Parecer';
    }

    public sign(item: any): void {
        if (this.isSigning) return;
        this.isSigning = true;
        this.signingParecerId = item.PPD_CodigoParecerDespacho;
        this.signingMessage = 'Gerando PDF para assinatura...';

        const origin: ParecerOrigin = item.__origin === 'interno' ? 'interno' : 'externo';
        const tipo: DocumentoTipo = item.__tipo || 'parecer';

        let prepare$;
        if (tipo === 'requerimento') {
            prepare$ = this.digitalFormService.prepareResponsibleSign(item.PPD_CodigoParecerDespacho);
        } else if (tipo === 'certidao') {
            prepare$ = this.processService.prepareCertidaoSecretarySign(item.PPD_CodigoParecerDespacho);
        } else if (origin === 'interno') {
            prepare$ = this.internalProcessService.prepareParecerSecretarySign(item.PPD_CodigoParecerDespacho);
        } else {
            prepare$ = this.processService.prepareParecerSecretarySign(item.PPD_CodigoParecerDespacho);
        }

        prepare$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: ({ pdfBase64, fileName, pageCount, signaturePosition }) => {
                    this.signingMessage = 'Aguardando assinatura no gov.br...';
                    this.openGovBrPopup(item.PPD_CodigoParecerDespacho, origin, tipo, pdfBase64, fileName, pageCount, signaturePosition);
                },
                error: (err) => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: err?.error?.message || 'Falha ao preparar PDF para assinatura.',
                    });
                },
            });
    }

    private endSigningFlow(): void {
        this.isSigning = false;
        this.signingMessage = '';
        this.signingParecerId = null;
    }

    private openGovBrPopup(
        parecerId: number,
        origin: ParecerOrigin,
        tipo: DocumentoTipo,
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        signaturePosition: { page: number; xPercentage: number; yPercentage: number } | null,
    ): void {
        const signUrl = `${environment.govBR.signUrl}/authorize?response_type=code&redirect_uri=${encodeURIComponent(
            environment.govBR.returnSignUrl,
        )}&scope=sign&client_id=${environment.govBR.signClientID}`;
        const width = 800;
        const height = 600;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const popup = window.open(signUrl, '_blank', `width=${width},height=${height},top=${top},left=${left}`);

        this.pendingSignContext = { parecerId, origin, tipo, pdfBase64, fileName, pageCount, signaturePosition };
        this.registerPostMessageFallback();

        const POLL_TIMEOUT_MS = 5 * 60 * 1000;
        const startedAt = Date.now();

        this.popupPollInterval = setInterval(() => {
            if (!popup || popup.closed) {
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    if (this.pendingSignContext) {
                        this.pendingSignContext = null;
                        this.endSigningFlow();
                        this.messageService.add({
                            severity: 'warn',
                            summary: 'Assinatura cancelada',
                            detail: 'A janela de assinatura foi fechada antes da conclusão.',
                        });
                    }
                });
                return;
            }

            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                try { popup.close(); } catch { /* noop */ }
                this.clearPopupPoll();
                this.ngZone.run(() => {
                    this.pendingSignContext = null;
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Tempo esgotado',
                        detail: 'A assinatura não foi concluída a tempo. Tente novamente.',
                    });
                });
                return;
            }

            try {
                if (popup.location.href.includes('code=')) {
                    const url = new URL(popup.location.href);
                    const code = url.searchParams.get('code');
                    popup.close();
                    this.clearPopupPoll();
                    this.ngZone.run(() => {
                        if (code && this.pendingSignContext) {
                            const ctx = this.pendingSignContext;
                            this.pendingSignContext = null;
                            this.callSignApi(code, ctx.parecerId, ctx.origin, ctx.tipo, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition);
                        } else {
                            this.endSigningFlow();
                        }
                    });
                }
            } catch {
                // cross-origin while popup is on gov.br domain — wait until same-origin redirect
            }
        }, 1000);
    }

    private registerPostMessageFallback(): void {
        if (this.boundMessageListener) return;

        this.boundMessageListener = (event: MessageEvent) => {
            try {
                const returnUrlOrigin = new URL(environment.govBR.returnSignUrl).origin;
                if (event.origin !== returnUrlOrigin) return;
                const code = (event.data && (event.data.code || event.data.signCode)) as string | undefined;
                if (!code || !this.pendingSignContext) return;
                const ctx = this.pendingSignContext;
                this.pendingSignContext = null;
                this.clearPopupPoll();
                this.ngZone.run(() => this.callSignApi(code, ctx.parecerId, ctx.origin, ctx.tipo, ctx.pdfBase64, ctx.fileName, ctx.pageCount, ctx.signaturePosition));
            } catch {
                /* noop */
            }
        };
        window.addEventListener('message', this.boundMessageListener);
    }

    private callSignApi(
        code: string,
        parecerId: number,
        origin: ParecerOrigin,
        tipo: DocumentoTipo,
        pdfBase64: string,
        fileName: string,
        pageCount: number,
        computedPosition: { page: number; xPercentage: number; yPercentage: number } | null,
    ): void {
        const lastPageIndex = Math.max(0, (pageCount || 1) - 1);
        const signaturePosition = computedPosition || { page: lastPageIndex, xPercentage: 50, yPercentage: 5 };
        this.signingMessage = 'Assinando documento...';
        this.documentSignatureService
            .signatureAndSaveDocument({
                code,
                fileName,
                pdfBase64,
                signaturePosition,
            })
            .subscribe(
                (response: any) => {
                    const signed = response as SignedFileDataResponse;
                    this.signingMessage = 'Finalizando assinatura...';
                    this.finalizeSign(parecerId, origin, tipo, signed.fileCloudFlare, fileName);
                },
                () => {
                    this.endSigningFlow();
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: 'Falha na assinatura via gov.br.',
                    });
                },
            );
    }

    private finalizeSign(
        parecerId: number,
        origin: ParecerOrigin,
        tipo: DocumentoTipo,
        fileCloudFlare: string,
        fileName: string,
    ): void {
        const userId = this.global.user()?.USR_CodigoUsuario;
        const payload = { fileCloudFlare, fileName: `${fileName}.pdf`, userId };

        let finalize$;
        if (tipo === 'requerimento') {
            finalize$ = this.digitalFormService.finalizeResponsibleSign(parecerId, fileCloudFlare, `${fileName}.pdf`);
        } else if (tipo === 'certidao') {
            finalize$ = this.processService.finalizeCertidaoSecretarySign(parecerId, payload);
        } else if (origin === 'interno') {
            finalize$ = this.internalProcessService.finalizeParecerSecretarySign(parecerId, payload);
        } else {
            finalize$ = this.processService.finalizeParecerSecretarySign(parecerId, payload);
        }

        finalize$
            .pipe(takeUntil(this.destroy$), finalize(() => this.endSigningFlow()))
            .subscribe({
                next: (response: any) => {
                    // No requerimento, a mensagem da API traz o processo aberto e o destino.
                    this.messageService.add({
                        severity: 'success',
                        icon: 'ph ph-check-circle',
                        closable: true,
                        life: tipo === 'requerimento' ? 8000 : 3000,
                        summary: 'Sucesso!',
                        detail:
                            tipo === 'requerimento'
                                ? response?.message || 'Requerimento assinado com sucesso.'
                                : `${this.tipoNome(tipo)} assinado(a) com sucesso.`,
                    });
                    this.listAll((this.pagination as any)?.current_page || 1);
                },
                error: (err) => {
                    this.messageService.add({
                        severity: 'error',
                        summary: 'Erro',
                        detail: err?.error?.message || 'Não foi possível finalizar a assinatura.',
                    });
                },
            });
    }

    private clearPopupPoll(): void {
        if (this.popupPollInterval) {
            clearInterval(this.popupPollInterval);
            this.popupPollInterval = null;
        }
        if (this.boundMessageListener) {
            window.removeEventListener('message', this.boundMessageListener);
            this.boundMessageListener = null;
        }
    }
}
